import { useGetCompaniesByOrgQuery, useGetCompanyQuery } from "@builder/store/slices/dataApiSlices/companiesApiSlice";
import { useGetCustomersQuery } from "@builder/store/slices/dataApiSlices/customersApiSlice";
import { useGetOrdersQuery } from "@builder/store/slices/dataApiSlices/ordersApiSlice";
import { useGetProductsQuery } from "@builder/store/slices/dataApiSlices/productsApiSlice";
import { useGetUsersQuery } from "@builder/store/slices/dataApiSlices/userApiSlice";
import { setIsOrg, setStoreData, setInitialLoad } from "@builder/store/slices/storeDataSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { FINAL_USERS, ORG_USERS, UserTypes } from "@builder/types/Users.type";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useMemo, useState } from "react";
import { Company } from "@builder/types/Company.type";
import { StoreData } from "@builder/store/slices/BuilderSlice/types";

interface SavedStoreData {
    company?: string;
    product?: string;
    order?: string;
    customer?: string;
    user?: string;
}

export const useInitStoreData = () => {
    const dispatch = useAppDispatch();
    const { user, data: storeData, isOrg } = useAppSelector(state => state.storeData);
    const [savedIds, setSavedIds] = useState<SavedStoreData | null>(null);
    const [hasLoadedFromStorage, setHasLoadedFromStorage] = useState(false);

    useEffect(() => {
        if (!user) return;
        if (FINAL_USERS.includes(user?.type as UserTypes) || ORG_USERS.includes(user?.type as UserTypes)) {
            dispatch(setIsOrg(true));
        } else {
            dispatch(setIsOrg(false));
        }
    }, [user, dispatch]);

    useEffect(() => {
        try {
            const savedData = localStorage.getItem("storeData");
            if (savedData) {
                const parsedData: SavedStoreData = JSON.parse(savedData);
                if (parsedData && Object.keys(parsedData).length > 0) {
                    setSavedIds(parsedData);
                }
            }
        } catch (error) {
            console.error("Error loading store data from localStorage:", error);
        } finally {
            setHasLoadedFromStorage(true);
        }
    }, []);

    const { data: orgCompanies, isLoading: orgCompaniesLoading } = useGetCompaniesByOrgQuery(
        isOrg && user?.companies?.[0]?.$oid ? user.companies[0].$oid : skipToken
    );
    const { data: realCompany, isLoading: realCompanyLoading } = useGetCompanyQuery(
        isOrg !== null && !isOrg && user?.companies?.[0] ? user.companies[0].$oid : skipToken
    );
    const { data: demoCompany, isLoading: demoCompanyLoading } = useGetCompanyQuery(
        isOrg !== null && !isOrg && user?.companies?.[1] ? user.companies[1].$oid : skipToken
    );

    const companiesArray = useMemo(() => {
        if (isOrg) {
            return orgCompanies || [];
        } else if (isOrg !== null) {
            return [realCompany, demoCompany].filter(Boolean) as Company[];
        }
        return [];
    }, [isOrg, orgCompanies, realCompany, demoCompany]);

    const companiesLoading = isOrg ? orgCompaniesLoading : realCompanyLoading || demoCompanyLoading;

    useEffect(() => {
        if (!hasLoadedFromStorage || isOrg === null || companiesLoading || !user) return;
        if (storeData?.company) return;

        let selectedCompany: Company | undefined;

        if (savedIds?.company) {
            selectedCompany = companiesArray.find(c => c._id === savedIds.company);
        }

        if (!selectedCompany && companiesArray.length > 0) {
            selectedCompany = companiesArray[0];
        }

        if (selectedCompany) {
            dispatch(setStoreData({ company: selectedCompany }));
        }
    }, [hasLoadedFromStorage, isOrg, companiesLoading, companiesArray, savedIds, storeData?.company, user, dispatch]);

    const { data: products, isLoading: productsLoading } = useGetProductsQuery(
        storeData?.company?._id ? { companyId: storeData.company._id, ...(isOrg && { organizationId: storeData.company.organizationId }) } : skipToken
    );
    const { data: orders, isLoading: ordersLoading } = useGetOrdersQuery(
        storeData?.company?._id ? { companyId: storeData.company._id, ...(isOrg && { organizationId: storeData.company.organizationId }) } : skipToken
    );
    const { data: customers, isLoading: customersLoading } = useGetCustomersQuery(
        storeData?.company?._id ? { companyId: storeData.company._id, ...(isOrg && { organizationId: storeData.company.organizationId }) } : skipToken
    );
    const { data: users, isLoading: usersLoading } = useGetUsersQuery(
        storeData?.company?._id ? { companyId: storeData.company._id, ...(isOrg && { organizationId: storeData.company.organizationId }) } : skipToken
    );

    useEffect(() => {
        if (!storeData?.company) return;
        if (productsLoading || ordersLoading || customersLoading || usersLoading) return;

        const updates: Partial<StoreData> = {};

        if (savedIds?.product) {
            const matchedProduct = products?.find(p => p._id === savedIds.product);
            updates.product = matchedProduct || products?.[0];
        } else if (products && products.length > 0) {
            updates.product = products[0];
        }

        if (savedIds?.order) {
            const matchedOrder = orders?.find(o => o._id === savedIds.order);
            updates.order = matchedOrder || orders?.[0];
        } else if (orders && orders.length > 0) {
            updates.order = orders[0];
        }

        if (savedIds?.customer) {
            const matchedCustomer = customers?.find(c => c._id === savedIds.customer);
            updates.customer = matchedCustomer || customers?.[0];
        } else if (customers && customers.length > 0) {
            updates.customer = customers[0];
        }

        if (savedIds?.user) {
            const matchedUser = users?.find(u => u._id === savedIds.user);
            updates.user = matchedUser || users?.[0];
        } else if (users && users.length > 0) {
            updates.user = users[0];
        }

        if (Object.keys(updates).length > 0) {
            dispatch(setStoreData(updates));
        }
    }, [products, orders, customers, users, productsLoading, ordersLoading, customersLoading, usersLoading, savedIds, storeData?.company, dispatch]);

    useEffect(() => {
        if (!hasLoadedFromStorage) return;
        if (isOrg === null || companiesLoading) return;
        if (!storeData?.company) {
            dispatch(setInitialLoad(false));
            return;
        }
        if (productsLoading || ordersLoading || customersLoading || usersLoading) return;

        dispatch(setInitialLoad(false));
    }, [hasLoadedFromStorage, isOrg, companiesLoading, storeData?.company, productsLoading, ordersLoading, customersLoading, usersLoading, dispatch]);
};
